import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

interface StudentActivityAnalyticsProps {
  courseId: string;
}

const StudentActivityAnalytics: React.FC<StudentActivityAnalyticsProps> = ({ courseId }) => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('monthly');

  // Mock data
  const engagementTrend = [
    { week: 'Week 1', dau: 145, wau: 189, mau: 220 },
    { week: 'Week 2', dau: 152, wau: 195, mau: 230 },
    { week: 'Week 3', dau: 148, wau: 192, mau: 225 },
    { week: 'Week 4', dau: 165, wau: 201, mau: 235 },
    { week: 'Week 5', dau: 171, wau: 208, mau: 240 },
  ];

  const sessionDuration = [
    { range: '0-15 min', count: 25 },
    { range: '15-30 min', count: 68 },
    { range: '30-60 min', count: 95 },
    { range: '60-90 min', count: 42 },
    { range: '90+ min', count: 15 },
  ];

  const studyPatterns = [
    { hour: '6-8', count: 12 },
    { hour: '8-10', count: 45 },
    { hour: '10-12', count: 68 },
    { hour: '12-14', count: 52 },
    { hour: '14-16', count: 38 },
    { hour: '16-18', count: 42 },
    { hour: '18-20', count: 58 },
    { hour: '20-22', count: 35 },
  ];

  const learningPace = [
    { category: 'Fast Learners', count: 45, color: '#82ca9d' },
    { category: 'Average Pace', count: 128, color: '#8884d8' },
    { category: 'Slow Learners', count: 72, color: '#ffc658' },
  ];

  const struggleIndicators = [
    { topic: 'Trigonometry', excessiveTime: 15, abandonment: 12, lowScores: 18 },
    { topic: 'Calculus Basics', excessiveTime: 18, abandonment: 15, lowScores: 22 },
    { topic: 'Polynomials', excessiveTime: 8, abandonment: 8, lowScores: 12 },
  ];

  const progressTrends = {
    accelerating: 68,
    decelerating: 25,
    stalled: 12,
    consistent: 140,
  };

  const contentInteraction = {
    videosWatched: 1245,
    quizzesAttempted: 892,
    assignmentsSubmitted: 456,
    topicsAccessed: 678,
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Student Activity Analysis
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Engagement Metrics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Daily Active Users
              </Typography>
              <Typography variant="h4">171</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Weekly Active Users
              </Typography>
              <Typography variant="h4">208</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Monthly Active Users
              </Typography>
              <Typography variant="h4">240</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Engagement Trend */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Engagement Trends (DAU/WAU/MAU)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={engagementTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="dau" stroke="#8884d8" strokeWidth={2} name="DAU" />
                <Line type="monotone" dataKey="wau" stroke="#82ca9d" strokeWidth={2} name="WAU" />
                <Line type="monotone" dataKey="mau" stroke="#ffc658" strokeWidth={2} name="MAU" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Session Duration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Session Duration Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sessionDuration}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Average Session Duration: <strong>42 minutes</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Median Session Duration: <strong>38 minutes</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Study Patterns */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Peak Study Times
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={studyPatterns}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Learning Pace */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Learning Pace Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={learningPace}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8">
                  {learningPace.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Progress Trends */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Progress Trends
            </Typography>
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Accelerating</Typography>
                <Typography variant="body2" fontWeight="bold">{progressTrends.accelerating}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Decelerating</Typography>
                <Typography variant="body2" fontWeight="bold">{progressTrends.decelerating}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Stalled</Typography>
                <Typography variant="body2" fontWeight="bold">{progressTrends.stalled}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Consistent</Typography>
                <Typography variant="body2" fontWeight="bold">{progressTrends.consistent}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Content Interaction */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Content Interaction
            </Typography>
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Videos Watched</Typography>
                <Typography variant="body2" fontWeight="bold">{contentInteraction.videosWatched}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Quizzes Attempted</Typography>
                <Typography variant="body2" fontWeight="bold">{contentInteraction.quizzesAttempted}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Assignments Submitted</Typography>
                <Typography variant="body2" fontWeight="bold">{contentInteraction.assignmentsSubmitted}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Topics Accessed</Typography>
                <Typography variant="body2" fontWeight="bold">{contentInteraction.topicsAccessed}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Struggle Indicators */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Struggle Indicators
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Excessive Time</TableCell>
                    <TableCell align="right">High Abandonment</TableCell>
                    <TableCell align="right">Low Scores</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {struggleIndicators.map((indicator, index) => (
                    <TableRow key={index}>
                      <TableCell>{indicator.topic}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${indicator.excessiveTime} students`} color="warning" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={`${indicator.abandonment}%`} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={`${indicator.lowScores} students`} color="error" size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Activity Timeline Link */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" gutterBottom>
                  Activity Timeline
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View detailed chronological activity timeline for students
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={() => navigate(`/admin/courses/${courseId}/dashboard/activity-timeline`)}
              >
                View Timeline
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentActivityAnalytics;

